import { useId } from 'react';
import { Segmented } from '@/components/ui/kit';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import agentPermissionsCopy from '@/i18n/ns/agent-permissions';
import { AllowedWell } from './allowed-well';
import type { ApprovalMode, KnowledgeClientGrant } from './types';

/** Families whose actions can run without asking, each with its own mode and allowed list. */
export const MODED_FAMILIES = ['library:write', 'data:connections'] as const;
export const isModedFamily = (id: string) => (MODED_FAMILIES as readonly string[]).includes(id);

/** What a family's switch needs to show: the page's own copy for it, already in words. */
export interface FamilySwitch {
  id: string;
  title: string;
  describe: string;
}

/** The scope switch: the whole row is the control. */
export function ScopeSwitch({
  title: heading,
  describe,
  on,
  busy,
  onChange,
}: {
  title: string;
  describe: string;
  on: boolean;
  /** A change is in flight: the switch keeps focus and ignores presses. */
  busy: boolean;
  onChange: () => void;
}) {
  const title = useId();
  const description = useId();
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-labelledby={title}
      aria-describedby={description}
      aria-disabled={busy}
      className="ws-agent-toggle"
      onClick={() => {
        if (!busy) onChange();
      }}
    >
      <span className="ws-agent-toggle-copy">
        <strong id={title}>{heading}</strong>
        <small id={description}>{describe}</small>
      </span>
      <span className="ws-agent-switch" aria-hidden="true">
        <span />
      </span>
    </button>
  );
}

function WarningMark() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 3 2.5 20h19z" />
      <path d="M12 10v4M12 17h.01" />
    </svg>
  );
}

/**
 * One permission family: its switch, and for a family with modes the choice
 * between asking each time and acting automatically, with the list of what may
 * run without asking when it asks.
 */
export function FamilyRow({
  grant,
  option,
  app,
  on,
  busy,
  onChange,
  onMode,
  onNotice,
  onChanged,
}: {
  grant: KnowledgeClientGrant;
  option: FamilySwitch;
  app: string;
  on: boolean;
  busy: boolean;
  onChange: () => void;
  onMode: (family: string, mode: ApprovalMode) => void;
  onNotice: (text: string, tone: 'ok' | 'error') => void;
  /** The grant as the server now holds it, after an allowed action changed. */
  onChanged: (client: KnowledgeClientGrant) => void;
}) {
  const copy = useNamespaceCopy(agentPermissionsCopy);
  const moded = isModedFamily(option.id);
  const approval = grant.approval?.[option.id];
  // A family that is on shows its mode only once the server has reported one.
  const showMode = moded && (!on || approval !== undefined);
  const mode: ApprovalMode = approval?.mode ?? 'ask';
  const automatic = on && mode === 'automatic';
  const warning = option.id === 'data:connections' ? copy.automaticConnections : copy.automaticSave;
  return (
    <div className={`ws-agent-family pc-portal${on ? '' : ' is-off'}`} data-family={option.id}>
      <ScopeSwitch
        title={option.title}
        describe={option.describe}
        on={on}
        busy={busy}
        onChange={onChange}
      />
      {showMode && (
        <div className="ws-agent-mode" data-tone={automatic ? 'warning' : undefined}>
          <p className="ws-agent-mode-explain">
            {automatic && <WarningMark />}
            <span>{automatic ? formatCopy(warning, { app }) : copy.askExplain}</span>
          </p>
          <Segmented<ApprovalMode>
            label={formatCopy(copy.modeGroupLabel, { family: option.title })}
            options={[
              { value: 'ask', label: copy.modeAsk },
              { value: 'automatic', label: copy.modeAutomatic },
            ]}
            value={mode}
            disabled={!on}
            onChange={(next) => {
              if (!busy && next !== mode) onMode(option.id, next);
            }}
          />
        </div>
      )}
      {showMode && on && mode === 'ask' && approval && (
        <AllowedWell
          key={`${grant.grantId}:${option.id}`}
          grantId={grant.grantId}
          family={option.id}
          app={app}
          approval={approval}
          onNotice={onNotice}
          onChanged={onChanged}
        />
      )}
    </div>
  );
}
